import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { api, API_BASE_URL } from '../services/api'
import { useCurator } from '../contexts/CuratorContext'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Subscribes to the API's Server-Sent Events stream and reacts to server-side
 * events. Reconnect is handled by the native EventSource implementation with
 * exponential backoff.
 *
 * Recognised events:
 *   - `profile.changed` — invalidate React Query caches (client-side state
 *     reload).
 *   - `curator.openClient { clientId }` — куратор кликнул `cc:<id>` callback
 *     в tracking-summary; навигируем в Mini App на дневник этого клиента.
 *     Это закрывает граблю Telegram Desktop, который не пересоздаёт iframe
 *     при повторном deeplink-клике (см. SKILL.md #22b): сигнал идёт через
 *     наш бэкенд и не зависит от того, обновил ли Telegram start_param.
 */
export function useServerEvents(enabled: boolean) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { isCurator, switchToCuratorMode, exitClient } = useCurator()

  useEffect(() => {
    if (!enabled) return
    const token = api.getToken()
    if (!token) return

    const url = `${API_BASE_URL.replace(/\/$/, '')}/events/stream?token=${encodeURIComponent(token)}`
    const source = new EventSource(url)

    source.onmessage = (ev) => {
      let data: { type?: string; clientId?: string; level?: number; previousLevel?: number; coins?: number } | null = null
      try {
        data = JSON.parse(ev.data)
      } catch {
        return
      }
      if (!data || data.type === 'connected') return

      if (data.type === 'profile.changed') {
        queryClient.invalidateQueries()
        return
      }

      // #292: сервер сообщил о новом уровне — Layout ловит и показывает поп-ап.
      if (data.type === 'level.up' && typeof data.level === 'number') {
        window.dispatchEvent(new CustomEvent('tfa:level-up', { detail: data }))
        queryClient.invalidateQueries({ queryKey: ['user'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        return
      }

      if (data.type === 'leaderboard.changed') {
        // Точечно — только query Лидерборда, чтобы не дёргать всё.
        queryClient.invalidateQueries({ queryKey: ['leaderboard-v2'] })
        return
      }

      if (data.type === 'curator.openClient' && typeof data.clientId === 'string' && UUID_RE.test(data.clientId)) {
        const target = `/curator/clients/${data.clientId}`
        ;(async () => {
          // Тот же паттерн, что в useStartParamRouting: если Mini App
          // оказался в client-режиме (например после `forceMode='client'`),
          // надо принудительно перейти в curator-режим, иначе
          // /curator/clients/:id не матчится в client-Routes.
          if (!isCurator) {
            try { await switchToCuratorMode() } catch { /* ignore */ }
          } else {
            // Куратор внутри клиента (impersonation) — выходим, иначе
            // viewingClientId блокирует curator-Routes (App.tsx:122).
            exitClient()
          }
          navigate(target, { replace: true, state: { tab: 'diary' } })
        })()
      }
    }

    source.onerror = () => {
      // EventSource auto-reconnects with exponential backoff; nothing to do.
    }

    return () => {
      source.close()
    }
  }, [enabled, queryClient, navigate, isCurator, switchToCuratorMode, exitClient])
}
